import { useSelector } from 'react-redux';
import { CartoLayer } from '@deck.gl/carto';
import { selectSourceById } from '@carto/react-redux';
import { useCartoLayerProps } from '@carto/react-api';
import {
  getLineColorFunctionMapping,
  getLineWidthFunctionMapping,
  getFillColorFunctionMapping,
  getPointRadiusFunctionMapping,
} from 'components/contexts/LayerStyleReferenceMapping';
import { useAppState } from 'components/contexts/State';
import {
  getFormattedValue,
  getNameFormatterFromColumn,
  getValueFormatterFromColumn,
} from 'components/contexts/ColumnFormatterMapping';
import getPinnedTooltipCoordinate from 'utils/getPinnedTooltipCoordinate';

export const CUSTOMSQL_FIFTH_LAYER_ID = 'customSqlFifthLayer';

function CustomSqlFifthLayer() {
  const { customSqlFifthLayer } = useSelector((state) => state.carto.layers);
  const source = useSelector((state) =>
    selectSourceById(state, customSqlFifthLayer?.source)
  );
  const cartoLayerProps = useCartoLayerProps({ source });
  const viewState = useSelector((state) => state.carto.viewState);

  const { customSqlData, pinnedTooltip, setPinnedTooltip } = useAppState();

  if (customSqlFifthLayer && source) {
    return new CartoLayer({
      ...cartoLayerProps,
      id: CUSTOMSQL_FIFTH_LAYER_ID,

      stroked: false,
      pointRadiusUnits: 'pixels',
      lineWidthUnits: 'pixels',
      pickable: true,
      visible: customSqlFifthLayer.visible,
      opacity: customSqlFifthLayer.opacity,

      getFillColor: getFillColorFunctionMapping[customSqlFifthLayer.getFillColor]
        ? getFillColorFunctionMapping[customSqlFifthLayer.getFillColor]
        : [0, 0, 0],
      pointRadiusMinPixels: customSqlFifthLayer.pointRadiusMinPixels
        ? customSqlFifthLayer.pointRadiusMinPixels
        : 1,
      getPointRadius: (v) => {
        if (
          customSqlFifthLayer.getPointRadius === 'default_point'
        )
          return getPointRadiusFunctionMapping.default_point(v);
        if (
          customSqlFifthLayer.getPointRadius === 'secondary_point'
        )
          return getPointRadiusFunctionMapping.secondary_point(v, viewState.zoom);
        return 5;
      },

      getLineColor: getLineColorFunctionMapping[customSqlFifthLayer.getLineColor]
        ? getLineColorFunctionMapping[customSqlFifthLayer.getLineColor]
        : [0, 0, 0],
      lineWidthMinPixels: customSqlFifthLayer.lineWidthMinPixels
        ? customSqlFifthLayer.lineWidthMinPixels
        : 1,
      getLineWidth: (v) => {
        if (
          customSqlFifthLayer.getLineWidth === 'default_line'
        )
          return getLineWidthFunctionMapping.default_line(v);
        if (
          customSqlFifthLayer.getLineWidth === 'secondary_line'
        )
          return getLineWidthFunctionMapping.secondary_line(v, viewState.zoom);
        return 4;
      },

      pointType: customSqlFifthLayer.pointType ? customSqlFifthLayer.pointType : 'circle',

      onDataLoad: (data) => {
        cartoLayerProps.onDataLoad && cartoLayerProps.onDataLoad(data);
      },

      onClick: (info) => {
        if (info?.object && info?.object?.properties && customSqlData?.[4]?.properties?.tooltip) {
          const tooltip = customSqlData[4].properties.tooltip;
          const title = tooltip.title ? tooltip.title : 'Details';
          let subTitle = tooltip.subtitle ? tooltip.subtitle : '';
          if (
            tooltip.subtitle_column &&
            info?.object?.properties[tooltip.subtitle_column]
          ) {
            subTitle = info?.object?.properties[tooltip.subtitle_column];
          }
          let htmlString = '';
          htmlString += `<div class="deck-tooltip-title">${title}</div>`;
          if (subTitle) {
            htmlString += `<div class="deck-tooltip-subtitle">${subTitle}</div>`;
          }
          htmlString += `<div class="deck-hr"></div><div class="deck-tooltip-msg-container">`;
          tooltip.columns.forEach((column) => {
            htmlString += `<div class="deck-tooltip-msg-wrapper"><div class="deck-tooltip-msg-text-1">${
              getNameFormatterFromColumn(source?.data, column)
                ? getNameFormatterFromColumn(source?.data, column)
                : column.replace(/_/g, ' ')
            }</div><div class="deck-tooltip-msg-text-2">`;
            htmlString += getValueFormatterFromColumn(source?.data, column)
              ? new Function(
                  'return ' + getValueFormatterFromColumn(source?.data, column)
                )()(info?.object?.properties[column])
              : getFormattedValue(info?.object?.properties[column]);
            htmlString += `</div></div>`;
          });
          htmlString += `</div>`;

          if (pinnedTooltip?.html === htmlString) {
            setPinnedTooltip(null);
          } else {
             let locationType;
              if(title.toLowerCase().includes('intersection')) {
                locationType = 'Intersection';
              } else if (title.toLowerCase().includes('ramp')) {
                locationType = 'Ramp';
              } else if (title.toLowerCase().includes('segment')) {
                locationType = 'Segment';
              } else {
                locationType = null;
              }
              setPinnedTooltip({
                html: htmlString,
                postMileInfo: {
                  Postmile: info?.object?.properties?.Postmile,
                  Location_Type: locationType,
                },
                coordinate: getPinnedTooltipCoordinate(info),
              });
          }
        }
      },

      onHover: (info) => {
        if (info?.object && info?.object?.properties) {
          const tooltip = customSqlData[4].properties.tooltip;
          const title = tooltip.title ? tooltip.title : 'Details';
          let subTitle = tooltip.subtitle ? tooltip.subtitle : '';
          if (
            tooltip.subtitle_column &&
            info?.object?.properties[tooltip.subtitle_column]
          ) {
            subTitle = info?.object?.properties[tooltip.subtitle_column];
          }
          let htmlString = '';
          htmlString += `<div class="deck-tooltip-title">${title}</div>`;
          if (subTitle) {
            htmlString += `<div class="deck-tooltip-subtitle">${subTitle}</div>`;
          }
          htmlString += `<div class="deck-hr"></div><div class="deck-tooltip-msg-container">`;
          tooltip.columns.forEach((column) => {
            htmlString += `<div class="deck-tooltip-msg-wrapper"><div class="deck-tooltip-msg-text-1">${
              getNameFormatterFromColumn(source?.data, column)
                ? getNameFormatterFromColumn(source?.data, column)
                : column.replace(/_/g, ' ')
            }</div><div class="deck-tooltip-msg-text-2">`;
            htmlString += getValueFormatterFromColumn(source?.data, column)
              ? new Function(
                  'return ' + getValueFormatterFromColumn(source?.data, column)
                )()(info?.object?.properties[column])
              : getFormattedValue(info?.object?.properties[column]);
            htmlString += `</div></div>`;
          });
          htmlString += `</div>`;
          info.object = { html: htmlString };
        }
      },
    });
  }
}

export default CustomSqlFifthLayer;
